// FlowDocuments.tsx - one flow's files inside the flow modal: what the merchant
// uploaded or signed, and its Documents. A file-manager grid or a table, each
// opening the preview dialog.
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, LayoutGrid, List } from "lucide-react";
import { CustomDocName } from "@/components/application/CustomDocName";
import StatementPreviewDialog, {
  type PreviewDocument,
} from "@/components/application/StatementPreviewDialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { formatDateTime } from "@/utils/dateUtils";
import { formatFileSize } from "@/utils/formatFileSize";
import type { FlowDocument } from "../types";
import { flowActionErrorMessage } from "./flowActionError";
import { flowDocumentsQuery } from "./queries";

type ViewMode = "grid" | "table";

const GRID = "grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-3";

// One file can sit on two steps (an ID photo that is also the voided check).
const previewKey = (document: FlowDocument) => `${document.file_id}:${document.step_key}`;

/** Shown after a signed row's label. */
const SOURCE_MARKERS: Record<FlowDocument["source"], string | null> = {
  upload: null,
  signed: "Signed",
  document: null,
};

function documentLabel(document: FlowDocument): string {
  const marker = SOURCE_MARKERS[document.source];
  return marker ? `${document.step_label} · ${marker}` : document.step_label;
}

/** The row's label; a name staff typed reads as custom. */
function DocumentLabel({
  document,
  focusable = false,
}: {
  document: FlowDocument;
  focusable?: boolean;
}) {
  if (!document.custom) return <>{documentLabel(document)}</>;
  const marker = SOURCE_MARKERS[document.source];
  return (
    <>
      <CustomDocName name={document.step_label} focusable={focusable} />
      {marker ? ` · ${marker}` : null}
    </>
  );
}

export interface FlowDocumentsProps {
  borrowerId: number;
  flowId: number;
}

const FlowDocuments: React.FC<FlowDocumentsProps> = ({ borrowerId, flowId }) => {
  const documents = useQuery(flowDocumentsQuery(borrowerId, flowId));
  const [mode, setMode] = useState<ViewMode>("grid");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const previews = useMemo<PreviewDocument[]>(
    () =>
      (documents.data ?? []).map((document) => ({
        key: previewKey(document),
        filename: document.filename,
        file_url: document.file_url,
      })),
    [documents.data]
  );

  return (
    <section aria-label="Documents" className="min-w-0 space-y-2 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-medium text-muted-foreground">Documents</h3>
        <ToggleGroup
          type="single"
          size="sm"
          value={mode}
          onValueChange={(value) => value && setMode(value as ViewMode)}
          aria-label="Documents view"
        >
          <ToggleGroupItem value="grid" aria-label="Grid view">
            <LayoutGrid className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="table" aria-label="Table view">
            <List className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {documents.data ? (
        documents.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents yet.</p>
        ) : mode === "grid" ? (
          <DocumentGrid documents={documents.data} onOpen={setOpenKey} />
        ) : (
          <DocumentTable documents={documents.data} onOpen={setOpenKey} />
        )
      ) : documents.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load documents: {flowActionErrorMessage(documents.error)}
        </p>
      ) : (
        <div className={GRID} role="status" aria-label="Loading documents">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="aspect-square rounded-lg" />
          ))}
        </div>
      )}

      <StatementPreviewDialog
        documents={previews}
        activeKey={openKey}
        onClose={() => setOpenKey(null)}
      />
    </section>
  );
};

interface DocumentListProps {
  documents: readonly FlowDocument[];
  onOpen: (key: string) => void;
}

function DocumentGrid({ documents, onOpen }: DocumentListProps) {
  return (
    <ul className={GRID}>
      {documents.map((document) => (
        <li key={previewKey(document)} className="min-w-0">
          <button
            type="button"
            className="group w-full space-y-1 rounded-lg text-left focus-visible:outline-none"
            onClick={() => onOpen(previewKey(document))}
          >
            <span className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/40 transition-colors group-hover:border-foreground/25 group-focus-visible:ring-2 group-focus-visible:ring-ring">
              <Thumbnail document={document} />
            </span>
            <span className="block truncate text-xs font-medium" title={document.filename}>
              {document.filename}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              <DocumentLabel document={document} />
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** An image shows itself; a PDF, or an image whose link expired, shows a file icon. */
function Thumbnail({ document }: { document: FlowDocument }) {
  const [failed, setFailed] = useState(false);
  if (document.content_type.startsWith("image/") && document.file_url && !failed) {
    return (
      <img
        src={document.file_url}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return <FileText className="h-8 w-8 text-muted-foreground" aria-hidden />;
}

/** Fixed columns so it fits a phone: Name takes what is left and truncates. */
function DocumentTable({ documents, onOpen }: DocumentListProps) {
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="w-[35%]">Document</TableHead>
          <TableHead className="w-24 text-right">Size</TableHead>
          <TableHead className="hidden w-44 sm:table-cell">Added</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.map((document) => (
          <TableRow key={previewKey(document)}>
            <TableCell>
              <button
                type="button"
                className="block w-full truncate text-left font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                title={document.filename}
                onClick={() => onOpen(previewKey(document))}
              >
                {document.filename}
              </button>
            </TableCell>
            <TableCell
              className="truncate text-muted-foreground"
              title={document.custom ? undefined : documentLabel(document)}
            >
              <DocumentLabel document={document} focusable />
            </TableCell>
            <TableCell className="whitespace-nowrap text-right text-muted-foreground">
              {formatFileSize(document.size_bytes)}
            </TableCell>
            <TableCell className="hidden whitespace-nowrap text-muted-foreground sm:table-cell">
              {formatDateTime(document.uploaded_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default FlowDocuments;
